/**
 * Host-layer classification for the cross-host enter transition (WEB-473).
 *
 * Crossing between LOCI hosts (Apex ⇄ City ⇄ App ⇄ Studio) is a full-page hard
 * navigation — different subdomains, so there is no client-side router event to
 * hook. The new host instead detects "I was just navigated to from a *different*
 * LOCI host" by classifying `document.referrer`, and plays `LayerTransition`
 * once on mount as an enter transition.
 *
 * This module is the pure, host-string → layer mapping plus the per-layer
 * `LayerNode` (label / coordinate sub-line / accent tone / dusk wash) the atom
 * renders. Kept free of `window`/`document` so it is trivially unit-testable;
 * the hook (`useHostTransition`) supplies the live referrer + current host.
 *
 * Host shapes mirror `lib/cross-host-urls.ts` (production + `staging` prefixes,
 * `loci.localhost` for local dev):
 *   - apex  → `loci.city`              / `staging.loci.city`
 *   - city  → `<city>.loci.city`       / `staging-<city>.loci.city`
 *   - app   → `app.loci.city`          / `staging.app.loci.city`
 *
 * App and Studio share the `app.loci.city` host (Studio is a path under it), so
 * a *referrer* can only be resolved to apex / city / app from its hostname. The
 * *current* layer is always supplied explicitly by the shell that mounts the
 * transition (each shell already knows its own layer), which is what lets a
 * Studio arrival render the ember "Studio" node.
 */

import { CITY_EDITIONS, type CityCode, type CityEdition } from '@loci/city-editions';

import type { MotionTone } from '@/components/atoms/motion/tone';
import type { LayerNode } from '@/components/atoms/motion/LayerTransition';

// The registry only ships live editions (CLT for the MVP) while `CityCode` is
// the wider union — index through a partial record so lookups stay type-safe
// (mirrors `CITY_EDITION_LOOKUP` in `WayfinderSheet`).
const CITY_EDITION_LOOKUP: Partial<Record<CityCode, CityEdition>> = CITY_EDITIONS;

/**
 * Layers the transition can render. `apex` / `city` / `app` match
 * `GlobalNavLayer`; `studio` is the ember sub-surface of the app host (mirrors
 * `GLOBAL_NAV_ACCENT_VARS`'s `'studio'` accent variant).
 */
export type HostLayer = 'apex' | 'city' | 'app' | 'studio';

/** Layer resolved purely from a hostname — never `studio` (shares the app host). */
export type HostnameLayer = 'apex' | 'city' | 'app';

const KNOWN_CITY_CODES: ReadonlySet<string> = new Set(
  (Object.keys(CITY_EDITIONS) as CityCode[]).map((code) => code.toLowerCase()),
);

function rootDomain(): string {
  return process.env.NEXT_PUBLIC_LOCI_ROOT_DOMAIN ?? 'loci.city';
}

/**
 * Classify a hostname into a LOCI layer, or `null` if it is not a recognised
 * LOCI host (external referrer, an unknown subdomain, or a city subdomain whose
 * code is not in the live `CITY_EDITIONS` registry). Staging-aware: the
 * `staging.` / `staging-<city>.` prefixes resolve to the same layers.
 *
 * @param hostname Bare host portion only — e.g. `app.loci.city` (from
 *   `URL.host` / `window.location.host`), never a full URL with scheme or path.
 *   A `null`/`undefined`/empty value classifies to `null`.
 */
export function layerFromHostname(hostname: string | null | undefined): HostnameLayer | null {
  if (!hostname) return null;
  const host = hostname.toLowerCase();
  const domain = rootDomain().toLowerCase();

  if (host === domain || host === `staging.${domain}`) return 'apex';

  const appPrefixes = ['app.', 'staging.app.'];
  for (const prefix of appPrefixes) {
    if (host === `${prefix}${domain}`) return 'app';
  }

  // City: `<code>.loci.city` or `staging-<code>.loci.city`. Strip the domain
  // suffix, then the optional `staging-` prefix, and validate the remaining
  // single label against the live registry so an unknown subdomain (e.g.
  // `admin.loci.city`) is not mis-read as a city.
  const suffix = `.${domain}`;
  if (host.endsWith(suffix)) {
    const label = host.slice(0, -suffix.length);
    const cityCandidate = label.startsWith('staging-') ? label.slice('staging-'.length) : label;
    if (cityCandidate && !cityCandidate.includes('.') && KNOWN_CITY_CODES.has(cityCandidate)) {
      return 'city';
    }
  }

  return null;
}

interface LayerNodeBase {
  readonly label: string;
  readonly sub: string;
  readonly tone: MotionTone;
  readonly bg: string;
}

/**
 * Per-layer transition node presets. Dusk washes are intentionally hardcoded
 * dark gradients — this overlay is full-bleed and momentary, and the LOCI motion
 * layer is dark-first (the wash is the atmosphere of the *destination* host, not
 * a theme surface). Labels/sub-lines mirror the Wayfinder + coord-strip copy.
 */
const LAYER_NODES: Record<HostLayer, LayerNodeBase> = {
  apex: {
    label: 'LOCI',
    sub: 'NATIONAL · APEX',
    tone: 'amber',
    bg: 'linear-gradient(180deg,#2a2310,#0b0f18)',
  },
  city: {
    label: 'LOCI · CITY',
    sub: 'PUBLIC DISCOVERY',
    tone: 'teal',
    bg: 'linear-gradient(180deg,#07332f,#0b0f18)',
  },
  app: {
    label: 'YOUR APP',
    sub: 'SAVED · TICKETS',
    tone: 'teal',
    bg: 'linear-gradient(180deg,#07332f,#0b0f18)',
  },
  studio: {
    label: 'STUDIO',
    sub: 'CREATOR',
    tone: 'ember',
    bg: 'linear-gradient(180deg,#2a2310,#0b0f18)',
  },
};

/**
 * Build the `LayerNode` for a layer, optionally specialising the city/app
 * sub-line with the resolved city coordinates / name (e.g. `35.2271° N · …`).
 */
export function layerNode(layer: HostLayer, cityCode?: CityCode): LayerNode {
  const base = LAYER_NODES[layer];
  if (layer === 'city' && cityCode) {
    const edition = CITY_EDITION_LOOKUP[cityCode];
    if (edition) {
      return {
        ...base,
        label: `LOCI · ${cityCode.toUpperCase()}`,
        sub: edition.displayName.toUpperCase(),
      };
    }
  }
  if (layer === 'studio' && cityCode) {
    return { ...base, sub: `CREATOR · ${cityCode.toUpperCase()}` };
  }
  if (layer === 'app' && cityCode) {
    return { ...base, sub: `SAVED · TICKETS · ${cityCode.toUpperCase()}` };
  }
  return { ...base };
}
